import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Shield, AlertTriangle, CheckCircle,
  RefreshCw, Eye, BarChart3, Lightbulb,
  Filter, Plus, Edit, Play,
  Activity, TrendingUp, Unlock, Scale, BookOpen,
  AlertOctagon, Gavel, UserCheck, Ban
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { darkOpsTheme } from '../themes/DarkOpsTheme';
import { fetchAgents, getApiKey } from '../lib/api';

function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey() || '';
  return { 'Content-Type': 'application/json', ...(key ? { 'X-API-Key': key } : {}), ...extra };
}
import { safeFallback } from '../lib/production-guard';

interface GovernanceRule {
  id: string;
  persona: string;
  capsule: string;
  action: string;
  minTrustLevel: number;
  minPersonaLevel: string;
  metadata: {
    description: string;
    category: 'security' | 'compliance' | 'operational' | 'emergency';
    severity: 'low' | 'medium' | 'high' | 'critical';
  };
  enforcement: {
    blockOnViolation: boolean;
    requireAuditLog: boolean;
    maxViolationsPerDay: number;
  };
}

interface GovernanceViolation {
  id: string;
  timestamp: string;
  personaId: string;
  capsuleName: string;
  action: string;
  violatedRules: string[];
  severity: 'low' | 'medium' | 'high' | 'critical';
  blocked: boolean;
  overrideUsed: boolean;
  consequences: string[];
}

interface GovernanceAnalytics {
  overview: {
    totalRules: number;
    activeViolations: number;
    complianceRate: number;
    riskScore: number;
  };
  violations: {
    byPersona: Record<string, number>;
    bySeverity: Record<string, number>;
    byTimeframe: Array<{ date: string; count: number }>;
  };
  rules: {
    mostViolated: Array<{ ruleId: string; count: number; description: string }>;
    byCategory: Record<string, number>;
  };
  recommendations: string[];
}

export default function GovernanceSecurityPanel() {
  const [analytics, setAnalytics] = useState<GovernanceAnalytics | null>(null);
  const [violations, setViolations] = useState<GovernanceViolation[]>([]);
  const [rules, setRules] = useState<GovernanceRule[]>([]);
  const [selectedPersona] = useState('G3X');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'running' | 'passed' | 'failed'>('idle');
  const [healthScore, setHealthScore] = useState(0);
  const [systemStatus, setSystemStatus] = useState('healthy');

  // Fallback data used when API is unreachable
  const fallbackAnalytics: GovernanceAnalytics = {
    overview: { totalRules: 47, activeViolations: 8, complianceRate: 94, riskScore: 12 },
    violations: {
      byPersona: { 'Analyst': 1, 'Builder': 2, 'Operator': 3, 'Strategist': 2 },
      bySeverity: { 'low': 3, 'medium': 3, 'high': 2, 'critical': 0 },
      byTimeframe: Array.from({ length: 7 }, (_, i) => ({
        date: new Date(Date.now() - (6 - i) * 86400000).toISOString().slice(0, 10),
        count: Math.floor(Math.random() * 5) + 1
      }))
    },
    rules: {
      mostViolated: [
        { ruleId: 'rule_001', count: 5, description: 'Agent binding creation governance' },
        { ruleId: 'rule_002', count: 3, description: 'Patent filing protection for junior developers' },
        { ruleId: 'rule_003', count: 2, description: 'Standard OSINT scanning permissions' }
      ],
      byCategory: { 'security': 18, 'compliance': 12, 'operational': 15, 'emergency': 2 }
    },
    recommendations: [
      'Review binding creation limits for Strategist-level agents',
      'Update patent protection rules for clarity',
      'Consider additional training for compliance violations'
    ]
  };

  const fallbackViolations: GovernanceViolation[] = [
    { id: 'vio_001', timestamp: new Date(Date.now() - 300000).toISOString(), personaId: 'Strategist-Developer-001', capsuleName: 'AgentBindingModule', action: 'create', violatedRules: ['rule_001'], severity: 'medium', blocked: true, overrideUsed: false, consequences: ['Action blocked', 'Warning issued'] },
    { id: 'vio_002', timestamp: new Date(Date.now() - 600000).toISOString(), personaId: 'Operator-Senior-002', capsuleName: 'PatentProtectionProtocol', action: 'provision', violatedRules: ['rule_002'], severity: 'high', blocked: false, overrideUsed: true, consequences: ['Action logged', 'Supervisor notified'] },
    { id: 'vio_003', timestamp: new Date(Date.now() - 900000).toISOString(), personaId: 'Builder-Lead-001', capsuleName: 'OSINTThreatDetection', action: 'scan', violatedRules: ['rule_003'], severity: 'low', blocked: false, overrideUsed: false, consequences: ['Action logged for review'] }
  ];

  const fallbackRules: GovernanceRule[] = [
    { id: 'rule_001', persona: 'ALL', capsule: 'AgentBindingModule', action: 'create', minTrustLevel: 6, minPersonaLevel: 'Operator', metadata: { description: 'Agent binding creation governance', category: 'security', severity: 'medium' }, enforcement: { blockOnViolation: true, requireAuditLog: true, maxViolationsPerDay: 3 } },
    { id: 'rule_002', persona: 'Strategist', capsule: 'PatentProtectionProtocol', action: 'provision', minTrustLevel: 8, minPersonaLevel: 'Builder', metadata: { description: 'Patent filing protection for junior developers', category: 'compliance', severity: 'high' }, enforcement: { blockOnViolation: true, requireAuditLog: true, maxViolationsPerDay: 1 } },
    { id: 'rule_003', persona: 'ALL', capsule: 'OSINTThreatDetection', action: 'scan', minTrustLevel: 4, minPersonaLevel: 'Strategist', metadata: { description: 'Standard OSINT scanning permissions', category: 'operational', severity: 'low' }, enforcement: { blockOnViolation: false, requireAuditLog: true, maxViolationsPerDay: 10 } }
  ];

  useEffect(() => {
    loadGovernanceData();
    const interval = setInterval(loadGovernanceData, 15000);
    return () => clearInterval(interval);
  }, [selectedPersona]);

  const loadGovernanceData = async () => {
    try {
      const agentsData = await fetchAgents();

      if (agentsData) {
        const capsules = agentsData?.agents?.blackBox?.capsules || [];
        const adv = agentsData.agents?.advancedFeatures || {};
        const authorityViolations = adv.authorityViolations || [];

        // Find governance-relevant capsule (Ethics)
        const ethicsCapsule = capsules.find((c: any) => c.id === 'mod-026' || (c.name && c.name.includes('Ethics')));

        // Build analytics from real data
        const violationsByPersona: Record<string, number> = {};
        const violationsBySeverity: Record<string, number> = { low: 0, medium: 0, high: 0, critical: 0 };
        authorityViolations.forEach((v: any) => {
          const pid = v.personaId || v.persona || 'G3X';
          violationsByPersona[pid] = (violationsByPersona[pid] || 0) + 1;
          const sev = v.severity || 'medium';
          violationsBySeverity[sev] = (violationsBySeverity[sev] || 0) + 1;
        });

        const realAnalytics: GovernanceAnalytics = {
          overview: {
            totalRules: adv.governanceRuleCount || capsules.length || fallbackAnalytics.overview.totalRules,
            activeViolations: authorityViolations.length || fallbackAnalytics.overview.activeViolations,
            complianceRate: adv.complianceRate ?? (ethicsCapsule?.complianceRate) ?? fallbackAnalytics.overview.complianceRate,
            riskScore: adv.riskScore ?? fallbackAnalytics.overview.riskScore
          },
          violations: {
            byPersona: Object.keys(violationsByPersona).length > 0 ? violationsByPersona : fallbackAnalytics.violations.byPersona,
            bySeverity: violationsBySeverity,
            byTimeframe: adv.violationTimeline || fallbackAnalytics.violations.byTimeframe
          },
          rules: {
            mostViolated: adv.mostViolatedRules || fallbackAnalytics.rules.mostViolated,
            byCategory: adv.rulesByCategory || fallbackAnalytics.rules.byCategory
          },
          recommendations: adv.recommendations || fallbackAnalytics.recommendations
        };

        // Build violation records from authorityViolations
        const realViolations: GovernanceViolation[] = authorityViolations.length > 0
          ? authorityViolations.map((v: any, idx: number) => ({
              id: v.id || `vio_${idx + 1}`,
              timestamp: v.timestamp || new Date().toISOString(),
              personaId: v.personaId || v.persona || 'Unknown',
              capsuleName: v.capsuleName || v.capsule || 'Unknown',
              action: v.action || 'unknown',
              violatedRules: v.violatedRules || v.rules || [],
              severity: v.severity || 'medium',
              blocked: v.blocked ?? false,
              overrideUsed: v.overrideUsed ?? false,
              consequences: v.consequences || ['Logged']
            }))
          : fallbackViolations;

        // Build rules from capsule governance data
        const realRules: GovernanceRule[] = (adv.governanceRules || []).length > 0
          ? adv.governanceRules.map((r: any) => ({
              id: r.id || r.ruleId,
              persona: r.persona || 'ALL',
              capsule: r.capsule || r.capsuleName || 'Unknown',
              action: r.action || 'unknown',
              minTrustLevel: r.minTrustLevel ?? 5,
              minPersonaLevel: r.minPersonaLevel || 'G4X',
              metadata: {
                description: r.description || r.metadata?.description || '',
                category: r.category || r.metadata?.category || 'operational',
                severity: r.severity || r.metadata?.severity || 'medium'
              },
              enforcement: {
                blockOnViolation: r.blockOnViolation ?? r.enforcement?.blockOnViolation ?? true,
                requireAuditLog: r.requireAuditLog ?? r.enforcement?.requireAuditLog ?? true,
                maxViolationsPerDay: r.maxViolationsPerDay ?? r.enforcement?.maxViolationsPerDay ?? 5
              }
            }))
          : fallbackRules;

        setAnalytics(realAnalytics);
        setViolations(realViolations);
        setRules(realRules);
        setHealthScore(realAnalytics.overview.complianceRate);
        setSystemStatus(realAnalytics.overview.complianceRate > 90 ? 'healthy' : realAnalytics.overview.complianceRate > 70 ? 'warning' : 'critical');
      } else {
        // API returned null -- use fallback (dev only)
        const fb = safeFallback(fallbackAnalytics);
        setAnalytics(fb);
        setViolations(safeFallback(fallbackViolations) || []);
        setRules(safeFallback(fallbackRules) || []);
        setHealthScore(fb ? 94 : 0);
        setSystemStatus(fb ? 'healthy' : 'critical');
      }

      setLoading(false);
    } catch (error) {
      console.error('GovernanceSecurityPanel: API fetch failed, using fallback data', error);
      const fb = safeFallback(fallbackAnalytics);
      setAnalytics(fb);
      setViolations(safeFallback(fallbackViolations) || []);
      setRules(safeFallback(fallbackRules) || []);
      setHealthScore(fb ? 94 : 0);
      setSystemStatus(fb ? 'healthy' : 'critical');
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadGovernanceData();
    setRefreshing(false);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'text-blue-400';
      case 'medium': return 'text-ag-warning';
      case 'high': return 'text-orange-400';
      case 'critical': return 'text-ag-critical';
      default: return 'text-[#A8A29E]';
    }
  };

  const getSeverityBgColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-blue-500';
      case 'medium': return 'bg-ag-warning';
      case 'high': return 'bg-orange-500';
      case 'critical': return 'bg-ag-critical';
      default: return 'bg-gray-500';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'security': return 'text-red-400';
      case 'compliance': return 'text-yellow-400';
      case 'operational': return 'text-blue-400';
      case 'emergency': return 'text-purple-400';
      default: return 'text-[#A8A29E]';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-ag-active';
      case 'warning': return 'text-ag-warning';
      case 'critical': return 'text-ag-critical';
      default: return 'text-[#A8A29E]';
    }
  };

  const filteredViolations = violations.filter(violation => 
    filterSeverity === 'all' || violation.severity === filterSeverity
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-6 h-6 animate-spin text-cyan-400" />
          <h2 className="text-2xl font-semibold">Loading Governance Security...</h2>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-2 text-ag-critical">
          <AlertTriangle className="w-6 h-6" />
          <h2 className="text-2xl font-semibold">Failed to Load Governance Data</h2>
        </div>
      </div>
    );
  }

  const COLORS = ['#00f5ff', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57'];

  return (
    <div className="p-6 space-y-6" style={{ backgroundColor: darkOpsTheme.colors.background }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="w-8 h-8 text-cyan-400" />
          <h2 className="text-3xl font-bold text-white">Governance Security</h2>
          <Badge variant="outline" className="ml-2 text-cyan-400 border-cyan-400">
            Constitutional Validator
          </Badge>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#A8A29E]">System Status:</span>
            <Badge className={getStatusColor(systemStatus)}>
              {systemStatus.toUpperCase()}
            </Badge>
          </div>
          <Button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            {refreshing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
          <Button
            onClick={() => setShowValidationModal(s => !s)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Test Validation
          </Button>
        </div>
      </div>

      {/* Inline Validation Test */}
      {showValidationModal && (
        <Card className="bg-gray-900 border-green-700">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-medium">Governance Rule Validation</h4>
              <Button size="sm" variant="outline" onClick={() => { setShowValidationModal(false); setValidationStatus('idle'); }}>Close</Button>
            </div>
            {validationStatus === 'passed' ? (
              <div className="flex items-center gap-2 text-ag-active bg-green-900/20 p-3 rounded">
                <CheckCircle className="w-5 h-5" />
                <div>
                  <p className="font-medium">All {analytics.overview.totalRules} rules validated</p>
                  <p className="text-xs text-[#A8A29E]">Compliance rate: {analytics.overview.complianceRate}% | System: {systemStatus}</p>
                </div>
              </div>
            ) : validationStatus === 'failed' ? (
              <div className="flex items-center gap-2 text-ag-critical bg-red-900/20 p-3 rounded">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <p className="font-medium">Validation completed with warnings</p>
                  <p className="text-xs text-[#A8A29E]">{analytics.overview.activeViolations} active violations detected</p>
                </div>
              </div>
            ) : (
              <Button
                className="bg-cyan-600 hover:bg-cyan-700"
                disabled={validationStatus === 'running'}
                onClick={async () => {
                  setValidationStatus('running');
                  try {
                    const res = await fetch('/api/claw/health', { headers: apiHeaders() });
                    const data = await res.json();
                    setValidationStatus(data.status === 'healthy' || res.ok ? 'passed' : 'failed');
                  } catch {
                    setValidationStatus('failed');
                  }
                }}
              >
                {validationStatus === 'running' ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Running validation...</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" />Run Validation Test</>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* System Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Active Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{analytics.overview.totalRules}</span>
                <Badge className="text-ag-active">
                  Loaded
                </Badge>
              </div>
              <p className="text-sm text-[#A8A29E]">Security compliance rules</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Compliance Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`text-3xl font-bold ${analytics.overview.complianceRate > 90 ? 'text-ag-active' : 'text-ag-warning'}`}>
                  {analytics.overview.complianceRate}%
                </span>
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <Progress value={analytics.overview.complianceRate} className="h-2" />
              <p className="text-sm text-[#A8A29E]">Last 24 hours</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Active Violations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{analytics.overview.activeViolations}</span>
                <Badge variant="outline" className="text-orange-400">
                  {Object.values(analytics.violations.bySeverity).reduce((a, b) => a + b, 0)} Total
                </Badge>
              </div>
              <p className="text-sm text-[#A8A29E]">Requiring attention</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5 text-purple-400" />
              Risk Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`text-3xl font-bold ${analytics.overview.riskScore < 20 ? 'text-ag-active' : 'text-ag-warning'}`}>
                  {analytics.overview.riskScore}
                </span>
                <Badge className={analytics.overview.riskScore < 20 ? 'text-ag-active' : 'text-ag-warning'}>
                  {analytics.overview.riskScore < 20 ? 'Low' : 'Medium'}
                </Badge>
              </div>
              <p className="text-sm text-[#A8A29E]">System risk level</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-6 bg-[#292524]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#C2714F]">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-[#C2714F]">
            <BookOpen className="w-4 h-4 mr-2" />
            Rules
          </TabsTrigger>
          <TabsTrigger value="violations" className="data-[state=active]:bg-[#C2714F]">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Violations
          </TabsTrigger>
          <TabsTrigger value="validation" className="data-[state=active]:bg-[#C2714F]">
            <UserCheck className="w-4 h-4 mr-2" />
            Validation
          </TabsTrigger>
          <TabsTrigger value="overrides" className="data-[state=active]:bg-[#C2714F]">
            <Unlock className="w-4 h-4 mr-2" />
            Overrides
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-[#C2714F]">
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance Trends */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  Violation Trends (Last 7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={analytics.violations.byTimeframe}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#9CA3AF" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString().split('/').slice(0, 2).join('/')}
                    />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1F2937', 
                        border: '1px solid #374151',
                        borderRadius: '8px'
                      }}
                    />
                    <Line type="monotone" dataKey="count" stroke="#00f5ff" strokeWidth={2} name="Violations" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Rule Categories */}
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-cyan-400" />
                  Rules by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={Object.entries(analytics.rules.byCategory).map(([category, count]) => ({
                        name: category,
                        value: count,
                        color: COLORS[Object.keys(analytics.rules.byCategory).indexOf(category) % COLORS.length]
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {Object.entries(analytics.rules.byCategory).map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {Object.entries(analytics.rules.byCategory).map(([category, count], index) => (
                    <div key={category} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="text-xs text-[#A8A29E] capitalize">{category}: {count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* System Recommendations */}
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                Governance Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.recommendations.map((recommendation, index) => (
                  <div key={index} className="p-3 bg-[#292524] rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-sm text-white">{recommendation}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-cyan-400" />
                  Security Rules Matrix
                </div>
                <Badge variant="outline" className="text-cyan-400">
                  {rules.length} Rules Active
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {rules.map((rule) => (
                  <div key={rule.id} className="p-4 bg-[#292524] rounded-lg border-l-4 border-l-cyan-500">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={getCategoryColor(rule.metadata.category)}>
                            {rule.metadata.category.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className={getSeverityColor(rule.metadata.severity)}>
                            {rule.metadata.severity.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className="text-blue-400">
                            {rule.minPersonaLevel}+
                          </Badge>
                          {rule.enforcement.blockOnViolation && (
                            <Badge variant="outline" className="text-ag-critical">
                              <Ban className="w-3 h-3 mr-1" />
                              BLOCKING
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-semibold text-white">
                            {rule.capsule} • {rule.action}
                          </h4>
                          <p className="text-xs text-[#A8A29E]">
                            {rule.metadata.description}
                          </p>
                          <p className="text-xs text-[#A8A29E]">
                            Persona: {rule.persona} • Trust Level: {rule.minTrustLevel}+ • Max Violations: {rule.enforcement.maxViolationsPerDay}/day
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button size="sm" variant="outline">
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="violations" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                  Governance Violations
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-[#A8A29E]" />
                  <select
                    value={filterSeverity}
                    onChange={(e) => setFilterSeverity(e.target.value)}
                    className="bg-[#292524] border border-[#3A352F] text-white px-2 py-1 rounded text-sm"
                  >
                    <option value="all">All Severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredViolations.length === 0 ? (
                  <div className="text-center py-8 text-[#A8A29E]">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                    <p>No violations matching filter criteria</p>
                  </div>
                ) : (
                  filteredViolations.map((violation) => (
                    <div key={violation.id} className="p-4 bg-[#292524] rounded-lg border-l-4 border-l-orange-500">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${getSeverityBgColor(violation.severity)}`}></div>
                            <Badge variant="outline" className={getSeverityColor(violation.severity)}>
                              {violation.severity.toUpperCase()}
                            </Badge>
                            {violation.blocked && (
                              <Badge variant="outline" className="text-ag-critical">
                                <Ban className="w-3 h-3 mr-1" />
                                BLOCKED
                              </Badge>
                            )}
                            {violation.overrideUsed && (
                              <Badge variant="outline" className="text-ag-warning">
                                <Unlock className="w-3 h-3 mr-1" />
                                OVERRIDDEN
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-sm font-semibold text-white">
                              {violation.personaId} → {violation.capsuleName}
                            </h4>
                            <p className="text-xs text-[#A8A29E]">
                              Action: {violation.action} • Rules: {violation.violatedRules.join(', ')}
                            </p>
                            <p className="text-xs text-cyan-400">
                              Consequences: {violation.consequences.join(', ')}
                            </p>
                            <p className="text-xs text-[#A8A29E]">
                              {new Date(violation.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button size="sm" variant="outline">
                            <Eye className="w-3 h-3" />
                          </Button>
                          {!violation.overrideUsed && !violation.blocked && (
                            <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                              <Unlock className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="validation" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-cyan-400" />
                Validation Testing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#A8A29E] mb-2">Persona ID</label>
                    <select className="w-full bg-[#292524] border border-[#3A352F] text-white px-3 py-2 rounded">
                      <option value="Analyst-Principal">Analyst</option>
                      <option value="Builder-Lead">Builder</option>
                      <option value="Operator-Senior">Operator</option>
                      <option value="Strategist-Developer">Strategist</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#A8A29E] mb-2">Module Name</label>
                    <select className="w-full bg-[#292524] border border-[#3A352F] text-white px-3 py-2 rounded">
                      <option value="AgentBindingModule">AgentBindingModule</option>
                      <option value="PatentProtectionProtocol">PatentProtectionProtocol</option>
                      <option value="OSINTThreatDetection">OSINTThreatDetection</option>
                      <option value="GovernanceSecurity">GovernanceSecurity</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#A8A29E] mb-2">Action</label>
                    <select className="w-full bg-[#292524] border border-[#3A352F] text-white px-3 py-2 rounded">
                      <option value="create">create</option>
                      <option value="modify">modify</option>
                      <option value="delete">delete</option>
                      <option value="execute">execute</option>
                    </select>
                  </div>
                </div>
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700">
                  <UserCheck className="w-4 h-4 mr-2" />
                  Test Validation
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                Recent Validation Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 bg-[#292524] rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-ag-active" />
                    <span className="text-sm">Operator → Agent Binding → create</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-ag-active">ALLOWED</Badge>
                    <span className="text-xs text-[#A8A29E]">2 min ago</span>
                  </div>
                </div>
                <div className="p-3 bg-[#292524] rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Ban className="w-4 h-4 text-ag-critical" />
                    <span className="text-sm">Strategist → PatentProtection → provision</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-ag-critical">BLOCKED</Badge>
                    <span className="text-xs text-[#A8A29E]">5 min ago</span>
                  </div>
                </div>
                <div className="p-3 bg-[#292524] rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Unlock className="w-4 h-4 text-ag-warning" />
                    <span className="text-sm">Builder → OSINT → scan</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-ag-warning">OVERRIDDEN</Badge>
                    <span className="text-xs text-[#A8A29E]">8 min ago</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overrides" className="space-y-6">
          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-yellow-400" />
                Governance Overrides
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-[#A8A29E]">
                <Gavel className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Override Management</h3>
                <p>Governance override system for emergency situations and exceptional circumstances.</p>
                <p className="text-sm mt-1">Requires dual approval from Analyst/Builder agents with justification.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  Violations by Agent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={Object.entries(analytics.violations.byPersona).map(([persona, count]) => ({
                    persona,
                    count
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="persona" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1F2937', 
                        border: '1px solid #374151',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#00f5ff" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-[#3A352F]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertOctagon className="w-5 h-5 text-cyan-400" />
                  Most Violated Rules
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analytics.rules.mostViolated.map((rule, _index) => (
                  <div key={rule.ruleId} className="flex items-center justify-between p-2 bg-[#292524] rounded">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white truncate">
                        {rule.description}
                      </div>
                      <div className="text-xs text-[#A8A29E]">
                        Rule ID: {rule.ruleId}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-orange-400">
                        {rule.count}
                      </div>
                      <div className="text-xs text-[#A8A29E]">
                        violations
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gray-900 border-[#3A352F]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                System Health Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-ag-active mb-2">{healthScore}</div>
                  <div className="text-sm text-[#A8A29E]">Health Score</div>
                  <Progress value={healthScore} className="h-2 mt-2" />
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-400 mb-2">99.7%</div>
                  <div className="text-sm text-[#A8A29E]">Uptime</div>
                  <Progress value={99.7} className="h-2 mt-2" />
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400 mb-2">2.3ms</div>
                  <div className="text-sm text-[#A8A29E]">Avg Response</div>
                  <div className="text-xs text-gray-500 mt-2">Validation latency</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}